import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { AppConfig } from "@src/config/AppConfig";
import { GetMasterDataReasonCodeMappingsDTO } from "@src/domain/dto/masterData";
import type { MasterDataReasonCodeMapping } from "@src/domain/entities/masterData";
import { GetMasterDataReasonCodeMappingsUseCase } from "@src/application/useCases/masterData";
import { MasterDataReasonCodeMappingRepository } from "@src/infrastructure/repositories/masterData";
import {
  buildListFiltersKey,
} from "@src/presentation/components/common/filters";
import { useMasterDataListQuery } from "./masterData/useMasterDataListQuery";

export interface MasterDataReasonCodeMappingFilters {
  purchaserId: number | null;
  deductionTypeId: number | null;
  promoTypeId: number | null;
}

const EMPTY_FILTERS: MasterDataReasonCodeMappingFilters = {
  purchaserId: null,
  deductionTypeId: null,
  promoTypeId: null,
};

interface MasterDataReasonCodeMappingsDataContextValue {
  brandKey: string;
  reasonCodeMappings: MasterDataReasonCodeMapping[];
  loading: boolean;
  error: string | null;
  search: string;
  setSearch: (search: string) => void;
  filters: MasterDataReasonCodeMappingFilters;
  setFilters: (filters: MasterDataReasonCodeMappingFilters) => void;
  page: number;
  limit: number;
  total: number;
  setPage: (page: number) => void;
  refetch: () => void;
}

const MasterDataReasonCodeMappingsDataContext =
  createContext<MasterDataReasonCodeMappingsDataContextValue | null>(null);

// eslint-disable-next-line react-refresh/only-export-components
export function useMasterDataReasonCodeMappings(): MasterDataReasonCodeMappingsDataContextValue {
  const context = useContext(MasterDataReasonCodeMappingsDataContext);
  if (!context) {
    throw new Error(
      "useMasterDataReasonCodeMappings must be used within MasterDataReasonCodeMappingsDataProvider",
    );
  }
  return context;
}

interface MasterDataReasonCodeMappingsDataProviderProps {
  brandKey: string;
  children: ReactNode;
  defaultPageSize?: number;
}

export function MasterDataReasonCodeMappingsDataProvider({
  brandKey,
  children,
  defaultPageSize = AppConfig.getDefaultPaginationLimit(),
}: Readonly<MasterDataReasonCodeMappingsDataProviderProps>) {
  const [filters, setFiltersState] =
    useState<MasterDataReasonCodeMappingFilters>(EMPTY_FILTERS);

  const getMappingsUseCase = useMemo(
    () =>
      new GetMasterDataReasonCodeMappingsUseCase(
        new MasterDataReasonCodeMappingRepository(),
      ),
    [],
  );

  const filtersKey = useMemo(() => buildListFiltersKey(filters), [filters]);

  const fetchPage = useCallback(
    async (args: { search: string | null; page: number; limit: number }) =>
      getMappingsUseCase.execute(
        new GetMasterDataReasonCodeMappingsDTO({
          search: args.search,
          purchaserId: filters.purchaserId,
          deductionTypeId: filters.deductionTypeId,
          promoTypeId: filters.promoTypeId,
          page_number: args.page,
          page_size: args.limit,
        }),
      ),
    [filters, getMappingsUseCase],
  );

  const {
    items: reasonCodeMappings,
    loading,
    error,
    search,
    setSearch,
    page,
    setPage,
    limit,
    total,
    refetch,
  } = useMasterDataListQuery({
    brandKey,
    defaultPageSize,
    errorMessage: "Failed to load master data reason codes",
    cacheScope: "reason-code-mappings",
    filtersKey,
    fetchPage,
  });

  useEffect(() => {
    setFiltersState(EMPTY_FILTERS);
  }, [brandKey]);

  const setFilters = useCallback(
    (next: MasterDataReasonCodeMappingFilters) => {
      setPage(1);
      setFiltersState(next);
    },
    [setPage],
  );

  const value = useMemo(
    (): MasterDataReasonCodeMappingsDataContextValue => ({
      brandKey,
      reasonCodeMappings,
      loading,
      error,
      search,
      setSearch,
      filters,
      setFilters,
      page,
      limit,
      total,
      setPage,
      refetch,
    }),
    [
      brandKey,
      reasonCodeMappings,
      loading,
      error,
      search,
      setSearch,
      filters,
      setFilters,
      page,
      limit,
      total,
      setPage,
      refetch,
    ],
  );

  return (
    <MasterDataReasonCodeMappingsDataContext.Provider value={value}>
      {children}
    </MasterDataReasonCodeMappingsDataContext.Provider>
  );
}
